import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Vote, ShoppingBag, MapPin, QrCode, ArrowRight, Trophy, Gift } from "lucide-react";
import api from "@/lib/api";

export default function Landing() {
  const [boxes, setBoxes] = useState([]);
  const [editions, setEditions] = useState([]);
  const [pizzerias, setPizzerias] = useState([]);

  useEffect(() => {
    api.get("/boxes").then((r) => setBoxes(r.data));
    api.get("/editions").then((r) => setEditions(r.data)).catch(() => {});
    api.get("/pizzerias").then((r) => setPizzerias(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const collections = useMemo(() => {
    const map = {};
    boxes.forEach((b) => { (map[b.collection] = map[b.collection] || []).push(b); });
    return Object.entries(map);
  }, [boxes]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rosette opacity-[0.06]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <span className="eyebrow text-cc-red">Le concept qui cartonne</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] mt-4 cc-navy">
              MA PIZZA <span className="tricolore-text">ME RAPPORTE</span>.
            </h1>
            <p className="text-lg text-slate-600 mt-5 max-w-lg">
              Commandez une pizza dans nos boîtes collector à l'effigie de l'actu. Chaque série de boîtes a son <strong>jeu concours</strong> sur le site : faites-vous voter, grimpez au classement et gagnez la lumière.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/commander" data-testid="hero-order-btn" className="btn-cocarde inline-flex items-center gap-2">
                <ShoppingBag size={18} /> Commander ma pizza
              </Link>
              <Link to="/concours" data-testid="hero-contest-btn" className="btn-navy inline-flex items-center gap-2">
                <Trophy size={18} /> Voir les jeux concours
              </Link>
            </div>
            <p className="text-xs font-mono text-slate-400 mt-4">📸 Filmez-vous avec votre boîte · Partagez avec #cocardepizza</p>
          </div>

          <div className="fade-up">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-white ring-1 ring-slate-200">
              <video data-testid="hero-video" src="/assets/hero.mp4" autoPlay muted loop playsInline
                className="w-full h-full object-cover aspect-[4/5] sm:aspect-square" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-4 pt-10">
                <p className="text-white font-display text-xl leading-tight">Une pizza, une cocarde, une campagne.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="tricolore-bar h-1.5 w-full" />
      </section>

      {/* PIZZA SIGNATURE — LA COCARDE */}
      <section className="bg-cc-navy text-white py-16 lg:py-24 relative overflow-hidden grain">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="eyebrow text-cc-gold">La pizza signature</span>
            <h2 className="font-display text-5xl sm:text-6xl mt-3">LA <span className="tricolore-text">COCARDE</span></h2>
            <p className="text-white/70 mt-4 max-w-md">Base tomate, mozzarella fondante, une pointe de bleu et de jambon supérieur disposés en rosette : notre pizza tricolore emblématique, cuite au feu de bois. Le goût de la campagne… la vraie.</p>
            <Link to="/commander" data-testid="signature-order-btn" className="btn-cocarde mt-7 inline-flex items-center gap-2">
              <ShoppingBag size={18} /> Commander LA COCARDE
            </Link>
          </div>
          <div className="relative grid place-items-center min-h-[360px]">
            <span className="smoke" style={{ left: "26%", animationDelay: "0s" }} />
            <span className="smoke" style={{ left: "44%", animationDelay: "2.5s" }} />
            <span className="smoke" style={{ left: "60%", animationDelay: "4.5s" }} />
            <span className="smoke" style={{ left: "74%", animationDelay: "6s" }} />
            <div className="absolute z-0 w-64 h-64 rounded-full bg-cc-gold/10 blur-3xl" />
            <img data-testid="signature-pizza" src="/assets/pizza-cocarde.png" alt="Pizza La Cocarde"
              className="relative z-10 w-full max-w-md mx-auto object-contain animate-floaty"
              style={{ filter: "drop-shadow(0 30px 45px rgba(0,0,0,0.55))" }} />
          </div>
        </div>
      </section>

      {/* CONCEPT */}
      <section id="concept" className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 overflow-hidden">
        <img src="/assets/box-open.webp" alt="" className="pointer-events-none select-none absolute -right-24 top-6 w-[540px] opacity-[0.07] rotate-6 hidden lg:block" />
        <div className="text-center max-w-2xl mx-auto">
          <span className="eyebrow text-cc-blue">Le concept</span>
          <h2 className="font-display text-4xl sm:text-5xl cc-navy mt-3">COMMENT MA PIZZA ME RAPPORTE</h2>
          <p className="text-slate-600 mt-3">Une boîte à thème = une entrée dans le jeu concours de sa série.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { icon: ShoppingBag, t: "1. Commandez votre boîte", d: "Choisissez une série d'actu (élections, sport, coupe de France…) et la pizzeria partenaire la plus proche.", c: "var(--cc-blue)" },
            { icon: Camera, t: "2. Entrez dans le jeu", d: "Chaque série a son jeu concours : glissez votre tête dans l'encart de la boîte, 30 s pour convaincre.", c: "var(--cc-red)" },
            { icon: Gift, t: "3. Votre pizza rapporte", d: "Partagez votre lien avec #cocardepizza, faites-vous voter et grimpez au classement de votre édition.", c: "var(--cc-gold)" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }} className="card-cocarde p-8">
              <div className="w-14 h-14 rounded-2xl grid place-items-center mb-5" style={{ background: s.c }}>
                <s.icon className="text-white" size={26} />
              </div>
              <h3 className="font-display text-2xl cc-navy">{s.t}</h3>
              <p className="text-slate-600 mt-2">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SÉRIES DE BOÎTES */}
      <section id="boites" className="bg-cc-navy text-white py-16 lg:py-24 relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow text-cc-gold">Nos séries de boîtes</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">UNE SÉRIE, UN JEU CONCOURS</h2>
            <p className="text-white/60 mt-3">Chaque série suit un grand rendez-vous de l'actu et possède son propre jeu concours sur le site.</p>
          </div>

          <div className="space-y-12 mt-14">
            {collections.map(([collection, list]) => (
              <div key={collection}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px flex-1 bg-white/15" />
                  <h3 className="font-display text-2xl sm:text-3xl text-cc-gold whitespace-nowrap">{collection}</h3>
                  <span className="h-px flex-1 bg-white/15" />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  {list.map((b) => (
                    <div key={b.id} className="bg-white/5 border border-white/10 rounded-3xl p-6 flex flex-col items-center text-center backdrop-blur-sm">
                      <div className="h-56 w-full grid place-items-center">
                        <img src={b.image_url} alt={b.name} className="max-h-56 object-contain drop-shadow-2xl" />
                      </div>
                      <h4 className="font-display text-3xl mt-5">{b.name}</h4>
                      <p className="text-cc-gold font-bold italic">« {b.tagline} »</p>
                      <p className="text-white/60 mt-2 text-sm">{b.description}</p>
                      <Link to="/commander" data-testid={`box-order-${b.slug}`} className="btn-cocarde mt-5 text-sm">Commander cette boîte</Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-3 mt-12 text-white/70 text-sm">
            <QrCode size={20} className="text-cc-gold" /> Chaque boîte porte un QR code menant au jeu concours de sa série.
          </div>
        </div>
      </section>

      {/* JEUX CONCOURS TEASER — les éditions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="eyebrow text-cc-red">En direct</span>
            <h2 className="font-display text-4xl sm:text-5xl cc-navy mt-3">LES JEUX CONCOURS DU MOMENT</h2>
            <p className="text-slate-500 mt-2">Chaque série de boîtes a son édition — entrez pour découvrir ses candidats.</p>
          </div>
          <Link to="/concours" data-testid="leaderboard-see-all" className="font-bold cc-navy hover:text-cc-red inline-flex items-center gap-1">
            Toutes les éditions <ArrowRight size={18} />
          </Link>
        </div>
        {editions.length === 0 ? (
          <div className="card-cocarde p-10 mt-8 text-center">
            <p className="text-slate-500">Aucune édition pour l'instant. <Link to="/concours" className="text-cc-red font-bold">Découvrir les jeux concours</Link></p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {editions.map((ed, i) => (
              <Link key={ed.slug} to={`/concours/${ed.slug}`} data-testid={`teaser-edition-${ed.slug}`}
                className="group rounded-3xl p-6 text-white relative overflow-hidden transition-transform hover:-translate-y-1"
                style={{ background: i % 2 === 0 ? "linear-gradient(120deg,#091E42,#1D4ED8)" : "linear-gradient(120deg,#7f1020,#E11D48)" }}>
                <span className="eyebrow text-cc-gold flex items-center gap-2"><Trophy size={13} /> Édition</span>
                <h3 className="font-display text-2xl sm:text-3xl mt-2">{ed.name.toUpperCase()}</h3>
                <p className="text-white/70 text-sm mt-2 line-clamp-1">{ed.boxes.map((b) => b.name).join(" · ")}</p>
                <div className="flex items-center justify-between mt-5">
                  <span className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5 text-sm font-bold"><Trophy size={14} className="text-cc-gold" /> {ed.candidate_count} candidat{ed.candidate_count > 1 ? "s" : ""}</span>
                  <span className="inline-flex items-center gap-1 font-bold text-cc-gold group-hover:gap-2 transition-all">Entrer <ArrowRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* PIZZERIAS TEASER */}
      <section className="bg-slate-100 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow text-cc-blue">Annuaire partenaires</span>
            <h2 className="font-display text-4xl sm:text-5xl cc-navy mt-3">DES PIZZAIOLOS ENGAGÉS</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {pizzerias.map((p) => (
              <div key={p.id} className="card-cocarde overflow-hidden">
                <div className="h-40 overflow-hidden"><img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <h3 className="font-display text-2xl cc-navy">{p.name}</h3>
                  <p className="text-slate-500 text-sm flex items-center gap-1 mt-1"><MapPin size={14} /> {p.city}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/annuaire" data-testid="see-directory-btn" className="btn-navy inline-flex items-center gap-2">
              Voir tout l'annuaire <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
